"use client";

import { Checkbox as CheckboxPrimitive } from "@base-ui/react/checkbox";
import { CheckIcon, MinusIcon } from "@phosphor-icons/react";

import { cn } from "../../lib/utils";

/**
 * The Radix tri-state, kept as this component's public API.
 *
 * Base UI splits it into two props — `checked: boolean` and a separate
 * `indeterminate: boolean` — but 122 files already pass `"indeterminate"` here,
 * and every "select all" header in the app derives exactly this union. Changing
 * the caller-facing shape is a refactor of the app, not a migration of a
 * primitive, so the translation lives in the wrapper instead.
 */
type CheckedState = boolean | "indeterminate";

/**
 * Migrated from `@radix-ui/react-checkbox` to Base UI.
 *
 * Base UI keeps the accessibility contract Radix established — `role="checkbox"`
 * on the root, `aria-checked` of `"true"`/`"false"`/`"mixed"`, and an indicator
 * mounted when checked *or* indeterminate — which is what lets all 122 callers
 * and their tests go untouched. What it changes is everything underneath, and
 * each difference is absorbed here:
 *
 *  - **Tri-state.** `checked="indeterminate"` is split into `checked={false}`
 *    plus `indeterminate`, and `onCheckedChange` is narrowed back to the Radix
 *    signature. Base UI reports a plain `boolean` and adds a second
 *    `eventDetails` argument; a caller written as
 *    `onCheckedChange={setSomething}` would otherwise start receiving that
 *    object silently.
 *  - **Root element.** Base UI defaults to a `<span>` beside a hidden
 *    `<input type="checkbox">`. This renders a real `<button>` instead, via
 *    `render` + `nativeButton`, because Radix did — and the difference is not
 *    cosmetic. A bare `<span>` computes `display: inline`, so `size-4` is
 *    ignored outright: the box collapses and the line grows from 56px to 94px.
 *    `inline-flex` fixes the size but not the position — an empty inline-level
 *    span sits 2px below the line box where a `<button>` sits at 3px, and no
 *    `vertical-align` keyword lands on 3. Rendering the element Radix rendered
 *    reproduces the baseline exactly, and keeps `disabled:` working (a `:disabled`
 *    pseudo-class matches buttons, never spans). All 12 screenshots match with
 *    zero differing pixels.
 *  - **`id` and labels.** `nativeButton` also puts the caller's `id` back on the
 *    root rather than on the hidden input, which is where Radix had it — so
 *    `<label htmlFor>` keeps pointing straight at the `role="checkbox"` element.
 *  - **State attributes.** `data-state="checked|unchecked|indeterminate"`
 *    becomes three presence attributes, so the styling hooks below moved to
 *    `data-[checked]` / `data-[indeterminate]` to match.
 */
type CheckboxProps = Omit<
  CheckboxPrimitive.Root.Props,
  | "checked"
  | "defaultChecked"
  | "indeterminate"
  | "onCheckedChange"
  | "className"
> & {
  /** `true`, `false`, or `"indeterminate"` — the Radix tri-state. */
  checked?: CheckedState;
  defaultChecked?: CheckedState;
  /**
   * Called with the *next* state — always a `boolean`, never `"indeterminate"`.
   * Neither primitive lets a user reach the mixed state by clicking, so it is a
   * value a parent sets, not one the checkbox produces.
   *
   * Radix declared this with method syntax (`onCheckedChange?(checked:
   * CheckedState): void`), which TypeScript checks *bivariantly* — so the 240
   * handlers written as `(checked: boolean) => void` compiled even though the
   * declared parameter was wider than they accept. Typing the emitted value
   * honestly as `boolean` keeps those compiling for the right reason instead of
   * by a soundness hole, and handlers that do accept the full union stay
   * assignable, because a wider parameter always is.
   */
  onCheckedChange?: (checked: boolean) => void;
  /**
   * Narrowed to a string. Base UI also accepts a `(state) => string` callback
   * here, which `cn()` would silently drop on the floor rather than reject.
   */
  className?: string;
};

function Checkbox({
  className,
  checked,
  defaultChecked,
  onCheckedChange,
  ...props
}: CheckboxProps) {
  return (
    <CheckboxPrimitive.Root
      // oxlint-disable-next-line jsx-a11y/control-has-associated-label -- render template only; Base UI merges the caller's aria-label/aria-labelledby/id (for <label htmlFor>) onto it
      render={<button type="button" />}
      nativeButton
      data-slot="checkbox"
      checked={checked === "indeterminate" ? false : checked}
      defaultChecked={
        defaultChecked === "indeterminate" ? false : defaultChecked
      }
      indeterminate={
        checked === "indeterminate" || defaultChecked === "indeterminate"
      }
      // Drop Base UI's second `eventDetails` argument. Callers pass handlers
      // typed for Radix's single-argument signature, and many are written as a
      // bare setter, so forwarding the primitive's callback unchanged would
      // hand them an argument they never expected.
      onCheckedChange={(next) => onCheckedChange?.(next)}
      className={cn(
        "peer border-input dark:bg-input/30 data-[checked]:bg-primary data-[checked]:text-primary-foreground dark:data-[checked]:bg-primary data-[checked]:border-primary data-[indeterminate]:bg-primary data-[indeterminate]:text-primary-foreground data-[indeterminate]:border-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive size-4 shrink-0 rounded-[4px] border shadow-xs transition-shadow outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="flex items-center justify-center text-current transition-none"
      >
        {checked === "indeterminate" ? (
          <MinusIcon className="size-3.5" />
        ) : (
          <CheckIcon className="size-3.5" />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

export { Checkbox };
export type { CheckedState };
